import { useState, useEffect, type FC } from "react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import SessionStorageHelper from "../../utils/sessionStorageHelper";
import { toast } from "sonner";

// Utility function to format API values to user-friendly labels
const formatDisplayValue = (field: string, value: string): string => {
  const formatMaps: Record<string, Record<string, string>> = {
    diabetes_type: {
      type1: "Type 1 Diabetes",
      type2: "Type 2 Diabetes",
      prediabetic: "Prediabetes",
      gestational: "Gestational Diabetes",
    },
    weight_loss_goal: {
      yes: "Yes, I want to lose weight",
      no: "No weight loss goal",
      maintain: "Maintain current weight",
    },
    activity_level: {
      sedentary: "Sedentary (little to no exercise)",
      lightly_active: "Lightly Active (light exercise 1-3 days/week)",
      moderately_active: "Moderately Active (moderate exercise 3-5 days/week)",
      very_active: "Very Active (hard exercise 6-7 days/week)",
      extremely_active: "Extremely Active (very hard exercise, physical job)",
    },
    diet_adherence: {
      omnivore: "Omnivore (eat everything)",
      vegetarian: "Vegetarian",
      vegan: "Vegan",
      keto: "Ketogenic Diet",
      paleo: "Paleo Diet",
      mediterranean: "Mediterranean Diet",
    },
    intermittent_fasting: {
      no_eat_throughout_day: "No, I eat throughout the day",
      "16_8": "Yes, 16:8 method",
      "18_6": "Yes, 18:6 method",
      "20_4": "Yes, 20:4 method",
      omad: "Yes, One Meal A Day (OMAD)",
      "5_2": "Yes, 5:2 method",
    },
    race: {
      white: "White",
      black_african_american: "Black/African American",
      hispanic_latino: "Hispanic/Latino",
      asian: "Asian",
      native_american: "Native American",
      pacific_islander: "Pacific Islander",
      other: "Other",
    },
  };

  return formatMaps[field]?.[value] || value;
};

const AssessmentSummary: FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [assessmentData, setAssessmentData] = useState<any>(null);

  useEffect(() => {
    const data = SessionStorageHelper.getAllData();
    setAssessmentData(data);
  }, []);

  const handleSubmitAssessment = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if we have required data
      if (!assessmentData) {
        throw new Error("No assessment data found");
      }

      // Direct assessment submission without connectivity test

      await SessionStorageHelper.submitAssessment();
      toast.success("Assessment submitted successfully!");
      navigate("/glucera" + GluceraNavigationRoutes.thankyoupage);
    } catch (error) {
      toast.error(
        `Assessment submission failed: ${error instanceof Error ? error.message : "Unknown error"}. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!assessmentData) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-lg text-foreground">
          Loading assessment data...
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 p-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-orange-400">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-500">
              Bite Balance
            </span>
          </div>

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 12 of 12
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <div className="mb-6 space-y-2">
            <h1 className="text-center text-2xl font-bold text-foreground sm:text-3xl">
              Review Your Assessment
            </h1>
            <p className="text-center text-sm text-muted-foreground">
              Please review your information before submitting
            </p>
          </div>

          <div className="space-y-4 pb-4">
            {/* Personal Information */}
            <Card className="border-border bg-card p-4">
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                Personal Information
              </h3>

              {assessmentData.gender && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Gender:</span>
                  <span className="capitalize text-foreground">
                    {assessmentData.gender}
                  </span>
                </div>
              )}

              {assessmentData.race && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Race:</span>
                  <span className="text-foreground">
                    {formatDisplayValue("race", assessmentData.race)}
                  </span>
                </div>
              )}

              {assessmentData.date_of_birth && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Date of Birth:</span>
                  <span className="text-foreground">
                    {assessmentData.date_of_birth}
                  </span>
                </div>
              )}
            </Card>

            {/* Physical Measurements */}
            <Card className="border-border bg-card p-4">
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                Physical Measurements
              </h3>

              {(assessmentData.height_ft ?? assessmentData.height_cm) && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Height:</span>
                  <span className="text-foreground">
                    {assessmentData.height_ft
                      ? `${assessmentData.height_ft}'${assessmentData.height_inches ?? 0}"`
                      : ""}
                    {assessmentData.height_cm
                      ? `${assessmentData.height_cm} cm`
                      : ""}
                  </span>
                </div>
              )}

              {(assessmentData.weight_kg ?? assessmentData.weight_lbs) && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Weight:</span>
                  <span className="text-foreground">
                    {assessmentData.weight_kg
                      ? `${assessmentData.weight_kg} kg`
                      : ""}
                    {assessmentData.weight_lbs
                      ? `${assessmentData.weight_lbs} lbs`
                      : ""}
                  </span>
                </div>
              )}
            </Card>

            {/* Health Information */}
            <Card className="border-border bg-card p-4">
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                Health Information
              </h3>

              {assessmentData.diabetes_type && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Diabetes Type:</span>
                  <span className="text-foreground">
                    {formatDisplayValue(
                      "diabetes_type",
                      assessmentData.diabetes_type
                    )}
                  </span>
                </div>
              )}

              {assessmentData.weight_loss_goal && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">
                    Weight Loss Goal:
                  </span>
                  <span className="text-foreground">
                    {formatDisplayValue(
                      "weight_loss_goal",
                      assessmentData.weight_loss_goal
                    )}
                  </span>
                </div>
              )}

              {assessmentData.activity_level && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Activity Level:</span>
                  <span className="text-foreground">
                    {formatDisplayValue(
                      "activity_level",
                      assessmentData.activity_level
                    )}
                  </span>
                </div>
              )}

              {assessmentData.diet_adherence && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">
                    Diet Preference:
                  </span>
                  <span className="text-foreground">
                    {formatDisplayValue(
                      "diet_adherence",
                      assessmentData.diet_adherence
                    )}
                  </span>
                </div>
              )}

              {assessmentData.intermittent_fasting && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">
                    Intermittent Fasting:
                  </span>
                  <span className="text-foreground">
                    {formatDisplayValue(
                      "intermittent_fasting",
                      assessmentData.intermittent_fasting
                    )}
                  </span>
                </div>
              )}

              {assessmentData.fasting_window_type && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted-foreground">Fasting Window:</span>
                  <span className="text-foreground">
                    {assessmentData.fasting_window_type}
                  </span>
                </div>
              )}
            </Card>

            {/* Meal Information */}
            {assessmentData.meal_times &&
              assessmentData.meal_times.length > 0 && (
                <Card className="border-border bg-card p-4">
                  <h3 className="mb-3 text-lg font-semibold text-foreground">
                    Meal Times
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {assessmentData.meal_times.map((time: string) => (
                      <span
                        key={`meal-time-${time}`}
                        className="rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary"
                      >
                        {time}
                      </span>
                    ))}
                  </div>
                </Card>
              )}

            {/* Medication Information */}
            {assessmentData.medication_details &&
              assessmentData.medication_details.length > 0 && (
                <Card className="border-border bg-card p-4">
                  <h3 className="mb-3 text-lg font-semibold text-foreground">
                    Medications
                  </h3>
                  <div className="space-y-2">
                    {assessmentData.medication_details.map(
                      (med: any, index: number) => (
                        <div
                          key={`medication-${index}`}
                          className="rounded-lg border border-border bg-card p-4"
                        >
                          <div className="mb-3">
                            <h4 className="mb-3 text-base font-semibold text-foreground">
                              {med.name}
                            </h4>
                          </div>
                          {med.doses && med.doses.length > 0 ? (
                            <div className="space-y-4">
                              {med.doses.map((dose: any, doseIndex: number) => (
                                <div
                                  key={doseIndex}
                                  className="rounded-lg bg-muted/20 p-3"
                                >
                                  <div className="mb-2">
                                    <h5 className="text-sm font-medium text-foreground">
                                      Dose {doseIndex + 1}
                                    </h5>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="font-medium text-foreground">
                                        Frequency:
                                      </span>
                                      <span className="text-muted-foreground">
                                        {dose.frequency}
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="font-medium text-foreground">
                                        Time:
                                      </span>
                                      <span className="text-muted-foreground">
                                        {dose.time}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rounded-lg bg-muted/20 p-3">
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="font-medium text-foreground">
                                    Frequency:
                                  </span>
                                  <span className="text-muted-foreground">
                                    {med.frequency}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="font-medium text-foreground">
                                    Time:
                                  </span>
                                  <span className="text-muted-foreground">
                                    {med.time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </Card>
              )}
          </div>
        </div>

        {/* Footer Button */}
        <div className="pb-safe border-t p-4">
          <Button
            onClick={handleSubmitAssessment}
            disabled={isSubmitting}
            className="h-11 w-full rounded-xl bg-gradient-primary text-sm hover:opacity-90 disabled:opacity-50 sm:h-12 sm:text-base"
          >
            {isSubmitting ? "Submitting Assessment..." : "Submit Assessment"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentSummary;
